import { useEffect, useRef, useState } from "react";
import { scrollToSection } from "@/lib/scroll";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Hero — Kelo Diamonds cinematic brand statement.
 *
 * Layout goal (Rolex-inspired):
 *   • Fills the viewport BELOW the sticky navbar (no gap).
 *   • Video is the sole visual — no cards, no borders.
 *   • Overlay text bottom-left: KELO DIAMONDS · "Built Under Pressure" · tagline
 *     · outline champagne CTA "DESCUBRE MÁS".
 *   • Minimal scroll indicator (thin vertical line + chevron) bottom-center.
 *   • Mobile video source served via <source media> for lightweight iPhone loads.
 */
export default function Hero() {
  const { t } = useLanguage();
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const vid = videoRef.current;
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === "undefined") return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const p = vid?.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } else {
          vid?.pause();
        }
      },
      { threshold: 0.05 }
    );
    io.observe(section);

    const onVis = () => {
      if (document.visibilityState !== "visible") {
        vid?.pause();
      } else {
        const rect = section.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          const p = vid?.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const goDiscover = () => scrollToSection("#flagship");

  return (
    <section
      id="hero"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[#050505]"
      // Fill the remaining viewport below the 60px navbar (56 when condensed).
      style={{ height: "calc(100svh - 70px)", minHeight: "540px" }}
    >
      {/* Poster (LCP paint before video decodes) */}
      <img
        src="/videos/hero-brand-poster.jpg"
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: "center 42%" }}
      />

      {/* Cinematic video (mobile-first source) */}
      <video
        ref={videoRef}
        data-testid="hero-video"
        className="hero-video-luxe absolute inset-0 h-full w-full object-cover"
        data-ready={videoReady ? "1" : "0"}
        poster="/videos/hero-brand-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onCanPlay={() => setVideoReady(true)}
        style={{ objectPosition: "center 42%" }}
      >
        <source
          src="/videos/hero-brand-mobile.mp4"
          type="video/mp4"
          media="(max-width: 767px)"
        />
        <source src="/videos/hero-brand.mp4" type="video/mp4" />
      </video>

      {/* Bottom scrim ONLY behind text — video breathes elsewhere */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-3/5 bg-gradient-to-t from-black/70 via-black/25 to-transparent"
      />

      {/* ── Overlay text (bottom-left, editorial) ─────────────────────── */}
      <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col items-start px-6 pb-[max(5.5rem,calc(3rem+env(safe-area-inset-bottom)))] sm:px-10 lg:px-14 lg:pb-24">
        <span
          className="hero-fade-up font-body text-[11px] font-medium uppercase tracking-[0.34em] text-white/85"
          style={{ animationDelay: "0.15s" }}
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </span>

        <h1
          className="hero-fade-up mt-3 max-w-[15ch] font-serif-display font-light leading-[0.98] tracking-[-0.01em] text-white"
          style={{
            animationDelay: "0.35s",
            fontSize: "clamp(2.6rem, 9vw, 5.75rem)",
          }}
          data-testid="hero-headline"
        >
          Built Under<br />Pressure
        </h1>

        <p
          className="hero-fade-up mt-4 max-w-sm font-body text-[13px] leading-relaxed text-white/85 sm:text-sm"
          style={{ animationDelay: "0.55s" }}
          data-testid="hero-tagline"
        >
          {t.hero.tagline}
        </p>

        <button
          type="button"
          onClick={goDiscover}
          data-testid="hero-discover-cta"
          className="hero-fade-up hero-cta-champagne group mt-7 inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-body text-[11px] font-medium uppercase tracking-[0.28em] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a26a]/40"
          style={{
            animationDelay: "0.75s",
            borderColor: "rgba(201,162,106,0.55)",
            color: "#e6cfa4",
            background: "rgba(0,0,0,0.12)",
          }}
        >
          <span>{t.hero.scrollCue}</span>
          <svg viewBox="0 0 20 20" className="hero-cta-arrow h-3 w-3" aria-hidden="true">
            <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* ── Scroll indicator (thin vertical line + chevron pill) ─────── */}
      <button
        type="button"
        onClick={goDiscover}
        aria-label={t.hero.scrollCue}
        data-testid="hero-scroll-indicator"
        className="hero-scroll-indicator absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 focus:outline-none sm:flex"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <span aria-hidden="true" className="hero-scroll-line block h-8 w-px bg-gradient-to-b from-transparent via-white/40 to-white/70" />
        <span aria-hidden="true" className="flex h-7 w-7 items-center justify-center rounded-full border border-white/45 bg-black/20 text-white/85 backdrop-blur-sm">
          <svg viewBox="0 0 20 20" className="h-3 w-3" aria-hidden="true">
            <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
    </section>
  );
}
